import { Box, Typography } from '@material-ui/core'
import { CustomTooltip } from 'components/facets/CustomTooltip'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { BulkSettings, Competitor, PricingStrategy } from 'shared'
import { EditPopper } from '../EditPopper'
import './editable-element.css'
export interface EditableElementProps {
  value: string | React.ReactNode
  options?: { value: number | string; label: string }[]
  tooltipText: string | React.ReactNode
  name: string
  viewOnly?: boolean
  title?: boolean
  attribute?: keyof Competitor | keyof PricingStrategy | keyof BulkSettings
  onChange: (
    newValue: unknown,
    attributeName?: keyof Competitor | keyof PricingStrategy | keyof BulkSettings
  ) => void
  type?: 'select' | 'multiple-select' | 'number' | 'multiple-textfield' | 'textfield' | 'bool'
  existingValue?: string | boolean | string[] | number
  viewOnlyTooltipText?: string | React.ReactNode
  sameTooltipForViewOnly?: boolean
  dataTestid?: string
  showBorder?: boolean
}

const TooltipWrapper = ({
  children,
  tooltipText,
  name,
  actionText,
  enterDelay,
}: {
  children: React.ReactElement
  tooltipText: React.ReactNode
  name: string
  actionText: string
  enterDelay?: number
}): JSX.Element => (
  <span>
    <CustomTooltip
      enterDelay={enterDelay}
      placement={'top'}
      title={
        <Box padding={1}>
          <Typography variant="body2" style={{ fontWeight: 'bold' }}>
            {name}
          </Typography>
          <Typography variant="body2">{tooltipText}</Typography>
          {actionText && (
            <Typography variant="body2" style={{ fontWeight: 'bold' }}>
              {actionText}
            </Typography>
          )}
        </Box>
      }
    >
      {children}
    </CustomTooltip>
  </span>
)

export const EditableElement = ({
  value,
  options = [],
  tooltipText,
  viewOnlyTooltipText,
  name,
  onChange,
  type = 'select',
  attribute,
  viewOnly,
  title = false,
  existingValue = '',
  showBorder = false,
  sameTooltipForViewOnly,
  dataTestid,
}: EditableElementProps): JSX.Element => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const { t } = useTranslation()
  let appliedTooltipText = tooltipText
  if (viewOnly && !sameTooltipForViewOnly) appliedTooltipText = viewOnlyTooltipText

  const handleClick = (event: React.MouseEvent<HTMLElement>): void => {
    if (viewOnly) return
    if (type === 'bool') onChange(!existingValue, attribute)
    else setAnchorEl(anchorEl ? null : event.currentTarget)
  }

  const editableTextClass = [
    'editable-element',
    title ? 'editable-element--title' : '',
    viewOnly ? 'editable-element--view-only' : '',
    viewOnly && !appliedTooltipText ? 'editable-element--plain' : '',
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <>
      <span
        className={`editable-element-wrapper ${
          !viewOnly && showBorder ? 'editable-element-wrapper--bordered' : ''
        }`}
        style={{ pointerEvents: appliedTooltipText ? 'initial' : 'none' }}
        onClick={handleClick}
        data-testid={`${dataTestid}`}
      >
        <TooltipWrapper
          actionText={viewOnly ? '' : t('common.clickToChange')}
          tooltipText={appliedTooltipText}
          name={name}
          enterDelay={250}
        >
          {typeof value === 'string' ? (
            <span className={editableTextClass}>{value}</span>
          ) : (
            <div>{value}</div>
          )}
        </TooltipWrapper>
      </span>
      {anchorEl && (
        <EditPopper
          onChange={(val) => onChange(val, attribute)}
          options={options}
          type={type}
          existingValue={existingValue}
          anchorEl={anchorEl}
          onDone={() => {
            setAnchorEl(null)
          }}
          dataTestId={`${dataTestid}-input`}
        />
      )}
    </>
  )
}
